


export type AudioErrorType = 
  | 'NETWORK_ERROR'
  | 'CODEC_ERROR' 
  | 'PERMISSION_ERROR'
  | 'PLAYBACK_ERROR'
  | 'TTS_ERROR'
  | 'UNKNOWN_ERROR';

export interface AudioError {
  type: AudioErrorType;
  message: string;
  originalError?: any;
  recoverable: boolean;
  retryable: boolean;
}

export class AudioErrorHandler {
  static categorizeError(error: any): AudioError {
    if (!error) {
      return {
        type: 'UNKNOWN_ERROR',
        message: 'Unknown audio error occurred',
        recoverable: false,
        retryable: true
      };
    }

    // Network related errors
    if (error.name === 'NetworkError' || error.message?.includes('network')) {
      return {
        type: 'NETWORK_ERROR',
        message: 'Network connection failed. Please check your internet connection.',
        originalError: error,
        recoverable: true,
        retryable: true
      };
    }

    // Permission errors
    if (error.name === 'NotAllowedError' || error.message?.includes('permission')) {
      return {
        type: 'PERMISSION_ERROR',
        message: 'Audio playback permission denied. Please allow audio in your browser.',
        originalError: error,
        recoverable: false,
        retryable: false
      };
    }

    // Codec/format errors
    if (error.name === 'NotSupportedError' || error.message?.includes('codec')) {
      return {
        type: 'CODEC_ERROR',
        message: 'Audio format not supported by your browser.',
        originalError: error,
        recoverable: false,
        retryable: false
      };
    }

    // TTS API errors
    if (error.message?.includes('speech') || error.message?.includes('TTS')) {
      return {
        type: 'TTS_ERROR',
        message: 'Text-to-speech service is temporarily unavailable.',
        originalError: error,
        recoverable: true,
        retryable: true
      };
    }

    // General playback errors
    return {
      type: 'PLAYBACK_ERROR',
      message: error.message || 'Audio playback failed. Please try again.',
      originalError: error,
      recoverable: true,
      retryable: true
    };
  }

  static getRetryDelay(retryCount: number): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, max 16s
    return Math.min(1000 * Math.pow(2, retryCount), 16000);
  }

  static shouldRetry(error: AudioError, retryCount: number): boolean {
    const maxRetries = 3;
    return error.retryable && retryCount < maxRetries;
  }

  static logError(error: AudioError, sessionId?: string) {
    console.error(
      `[${sessionId || 'AudioError'}] ${error.type}: ${error.message}`,
      error.originalError
    );
  }
}
